import { useState, useCallback, useMemo } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { nip19 } from 'nostr-tools';

import { Button } from '@/components/ui/button';
import { useProjectsManager } from '@/hooks/useProjectsManager';
import { useToast } from '@/hooks/useToast';
import type { RepositoryEvent } from '@/hooks/useNostrRepos';
import { cn, generateVividGradient } from '@/lib/utils';

import { GitBranch, Copy, Check, Folder } from 'lucide-react';

interface Props {
  repo: RepositoryEvent;
  index: number;
}

export default function ProfileViewRepositoryCard({ repo, index }: Props) {
  const projectsManager = useProjectsManager();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [isCloning, setIsCloning] = useState(false);
  const [copied, setCopied] = useState(false);

  const nameTag = repo.tags.find((tag) => tag[0] === 'name');
  const dTag = repo.tags.find((tag) => tag[0] === 'd');
  const descTag = repo.tags.find((tag) => tag[0] === 'description');
  const cloneTag = repo.tags.find((tag) => tag[0] === 'clone');

  const repoName = nameTag?.[1] || dTag?.[1] || 'Unnamed Repository';
  const projectSlug = dTag?.[1] || '';
  const description = descTag?.[1] || '';
  const cloneUrl = cloneTag?.[1] || (dTag?.[1] ? `nostr://${repo.pubkey}/${dTag[1]}` : '');

  const npub = useMemo(() => nip19.npubEncode(repo.pubkey), [repo.pubkey]);

  // Generate a deterministic vivid gradient based on the repo id
  const gradientStyle = useMemo(() => ({
    background: generateVividGradient(repo.id),
  }), [repo.id]);

  const handleClone = useCallback(async () => {
    if (!cloneUrl) return;

    setIsCloning(true);
    try {
      await projectsManager.init();
      const project = await projectsManager.cloneProject(repoName, cloneUrl);

      toast({
        title: 'Repository imported',
        description: `"${project.name}" is ready to edit.`,
      });

      navigate(`/project/${project.id}?build`);
    } catch (error) {
      console.error('Failed to clone repository:', error);
      toast({
        title: 'Failed to import repository',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setIsCloning(false);
    }
  }, [cloneUrl, navigate, projectsManager, repoName, toast]);

  const handleCopy = async () => {
    if (!cloneUrl) return;
    try {
      await navigator.clipboard.writeText(cloneUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (error) {
      console.error('Failed to copy URL:', error);
      toast({
        title: 'Failed to copy',
        description: 'Could not copy repository URL',
        variant: 'destructive',
      });
    }
  };

  const projectUrl = projectSlug ? `/${npub}/${projectSlug}` : undefined;

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.04 }}
      className="group"
    >
      <div className="relative rounded-2xl border border-border/60 bg-gradient-to-b from-card to-card/60 overflow-hidden shadow-sm transition-all duration-300 hover:-translate-y-1 hover:shadow-xl hover:shadow-primary/10">
        {/* Clickable gradient header with folder icon */}
        {projectUrl ? (
          <Link to={projectUrl} className="relative block aspect-[16/10] flex items-center justify-center" style={gradientStyle}>
            <Folder className="h-16 w-16 text-white/50" />
          </Link>
        ) : (
          <div className="relative aspect-[16/10] flex items-center justify-center" style={gradientStyle}>
            <Folder className="h-16 w-16 text-white/50" />
          </div>
        )}

        <div className="p-5 space-y-3">
          <div className="flex items-start justify-between gap-3">
            <div className="space-y-1 min-w-0">
              {projectUrl ? (
                <Link to={projectUrl}>
                  <h3 className="font-semibold text-base tracking-tight truncate group-hover:text-primary transition-colors hover:underline">
                    {repoName}
                  </h3>
                </Link>
              ) : (
                <h3 className="font-semibold text-base tracking-tight truncate group-hover:text-primary transition-colors">
                  {repoName}
                </h3>
              )}
              {description && (
                <p className="text-sm text-muted-foreground line-clamp-2 leading-relaxed">
                  {description}
                </p>
              )}
            </div>
            <div className="flex-shrink-0 rounded-xl bg-muted/70 p-2">
              <GitBranch className="h-4 w-4 text-muted-foreground" />
            </div>
          </div>

          <div className="flex items-center gap-2 pt-1">
            <Button
              size="sm"
              className="flex-1 h-9 rounded-xl text-sm shadow-sm"
              onClick={handleClone}
              disabled={isCloning || !cloneUrl}
            >
              <GitBranch className="h-3.5 w-3.5 mr-2" />
              Clone
            </Button>
            <Button
              size="sm"
              variant="outline"
              className={cn(
                'h-9 w-9 rounded-xl border-border/60 hover:border-primary/40 hover:bg-primary/5',
                copied && 'border-green-500/60 bg-green-500/10 text-green-600',
              )}
              onClick={handleCopy}
              disabled={!cloneUrl}
            >
              {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            </Button>
          </div>
        </div>
      </div>
    </motion.div>
  );
}
